/**
 * Eid Mubarak cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_ramadan_eid`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eidMubarakCardsPack: ToilePack = {
    slug: 'eid-mubarak-cards',
    category: 'season',
    name: 'Eid Mubarak cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'arch', label: 'Floral arch', subject: 'a vertical card with a pointed arch framed in roses and leaves, "Eid Mubarak" lettered inside the arch and an empty panel beneath for a message'},
        {key: 'crescent', label: 'Crescent', subject: 'a vertical card with a large crescent and hanging stars at the top and "Eid Mubarak" lettered across the middle, a slim border around the edge'},
        {key: 'adha', label: 'Eid al-Adha', subject: 'a vertical card lettered "Eid al-Adha Mubarak" above a mosque dome and minaret with a small sheep drawn on a green hill beside it'},
    ],
    styles: [
        {key: 'foil', label: 'Gold foil', style: 'metallic gold foil linework with fine ornament and a thin double border', palette: 'gold on midnight blue', framing: 'Centered on the card with an even margin around the border'},
        {key: 'papercut', label: 'Paper cut', style: 'flat layered paper-cut shapes in overlapping tones with clean edges', palette: 'teal, plum and gold', framing: 'Centered on the card with an even margin around the artwork'},
        {key: 'watercolor', label: 'Watercolor', style: 'loose watercolor illustration with soft bleeding edges and a hand-painted border', palette: 'indigo, blush and gold washes on textured white paper', framing: 'Full card with a painted border, any message area left as clean paper'},
    ],
    directions: ['Eid al-Fitr', 'for teacher', 'Arabic script'],
    directionHint: 'try: Eid al-Fitr',
    seo: {
        title: 'Eid Mubarak cards: floral arch, crescent, Eid al-Adha',
        metaDescription: 'Nine printable Eid Mubarak cards with a floral arch, a crescent or an Eid al-Adha scene, in gold foil, paper cut and watercolor.',
        h1: 'Eid Mubarak cards: floral arch, crescent, Eid al-Adha',
        tagline: 'Nine greeting cards, three designs each in gold foil, paper cut and watercolor.',
        intro: 'Printable greeting cards for Eid al-Fitr at the end of Ramadan, or for Eid al-Adha. The cards carry Eid Mubarak inside a pointed arch of roses and leaves, under a large crescent with hanging stars, or above a mosque dome with a small sheep on a green hill. Type a direction such as Eid al-Fitr or Arabic script and the card is drawn with it.',
        useCases: [
            'a card for family at the end of Ramadan',
            'a card for Eid al-Adha',
            'a greeting for a teacher or colleague',
            'a card for neighbours',
        ],
        faq: [
            {q: 'Which card is for Eid al-Adha?', a: 'The third design is lettered Eid al-Adha Mubarak above a mosque dome, with a small sheep on a green hill beside it.'},
            {q: 'Is there room for a message?', a: 'The floral arch card has an empty panel beneath the lettering for a message.'},
            {q: 'What are the three styles?', a: 'Gold foil is metallic linework with fine ornament, paper cut is flat layered shapes, and watercolor is a loose painted illustration.'},
            {q: 'Can I ask for Arabic script?', a: 'Yes. Type Arabic script as a direction and the card is drawn with it. That is a request, not a promised result.'},
        ],
    },
}
